// Geboortevenster · Viewmodel van het paneel: lanes en planeten → groepen voor hd-property-lanes (Basics, Planeten, Variabelen, Base).
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { PHS_DESIGN_ACCENT } from '../bodygraph/hd-phs-metadata';
import { getHdPlanetDisplayConfigByPlanetId } from '../bodygraph/planet-grid/hd-planet-display-config';
import { formatBirthClock, formatBirthWindowDuration, formatBirthWindowSignedDelta, sameBirthClockDay } from './birth-window-format';
import { HdBirthWindowPropertyMargin } from './birth-window-margins';
import {
  HD_BIRTH_WINDOW_TIERS,
  HdBirthWindowBlock,
  HdBirthWindowLane,
  HdBirthWindowPlanetChange,
  HdBirthWindowPlanetLane,
  HdBirthWindowTier,
} from './birth-window.models';
import { HdBirthWindowLabelService } from './hd-birth-window-label.service';
import {
  HdPropertyLaneBlockVm,
  HdPropertyLaneGroupVm,
  HdPropertyLaneMarkerVm,
  HdPropertyLaneTrackVm,
  HdPropertyLaneVm,
} from './property-lanes/hd-property-lanes.models';

/** Accentkleur per laag voor de groepskoppen en de marge-kaartjes. */
export const HD_BIRTH_WINDOW_TIER_COLORS: Readonly<Record<HdBirthWindowTier, string>> = {
  basics: '#d4a869',
  planets: '#1983bf',
  variables: '#773e9a',
  base: '#9a958b',
};

/** Spoorkleuren van de planetenlanes: personality zwart, design rood (HD-conventie). */
export const HD_BIRTH_WINDOW_PERSONALITY_ACCENT = '#3c4650';
export const HD_BIRTH_WINDOW_DESIGN_ACCENT = PHS_DESIGN_ACCENT;

export interface HdBirthWindowPanelVmContext {
  readonly lang: string;
  readonly timeZone: string | undefined;
  readonly birthMs: number;
  readonly labels: HdBirthWindowLabelService;
  readonly t: (key: string, params?: Record<string, unknown>) => string;
  /** Momentvenster op een bewaard moment (eigen laaguitleg). */
  readonly overlay?: boolean;
}

/** Het domeinblok reist mee in `data` zodat de popup de exacte tijden en oorzaken kan tonen. */
export interface HdBirthWindowBlockData {
  readonly kind: 'property';
  readonly lane: HdBirthWindowLane;
  readonly block: HdBirthWindowBlock;
}

/** Planeetblok: poort.lijn van één planeet op één kant. */
export interface HdBirthWindowPlanetBlockData {
  readonly kind: 'planet';
  readonly planetId: number;
  readonly sourceKey: string;
  readonly side: 'personality' | 'design';
  readonly gate: number;
  readonly line: number;
  readonly fromMs: number;
  readonly toMs: number;
  readonly state: HdBirthWindowBlock['state'];
}

export function formatBlockRange(block: { fromMs: number; toMs: number }, ctx: HdBirthWindowPanelVmContext): string {
  const withDate = !sameBirthClockDay(block.fromMs, ctx.birthMs, ctx.timeZone) || !sameBirthClockDay(block.toMs, ctx.birthMs, ctx.timeZone);
  const from = formatBirthClock(block.fromMs, ctx.timeZone, ctx.lang, { date: withDate });
  const to = formatBirthClock(block.toMs, ctx.timeZone, ctx.lang, { date: withDate });
  return `${from} – ${to} · ${formatBirthWindowDuration(block.toMs - block.fromMs, ctx.lang)}`;
}

function blockVm(lane: HdBirthWindowLane, block: HdBirthWindowBlock, ctx: HdBirthWindowPanelVmContext): HdPropertyLaneBlockVm {
  const propertyLabel = ctx.labels.propertyLabel(lane.key, ctx.lang);
  const label = ctx.labels.valueLabel(block.value.payload, ctx.lang);
  const parts = block.diff && block.value.payload.kind === 'list'
    ? ctx.labels.diffParts(block.diff, block.value.payload.itemKind, ctx.lang, true)
    : undefined;
  return {
    fromMs: block.fromMs,
    toMs: block.toMs,
    label,
    parts,
    title: `${propertyLabel}: ${ctx.labels.valueLabelFull(block.value.payload, ctx.lang)}\n${formatBlockRange(block, ctx)}`,
    state: block.state,
    data: { kind: 'property', lane, block } satisfies HdBirthWindowBlockData,
  };
}

function laneHint(lane: HdBirthWindowLane, estimateMargins: readonly HdBirthWindowPropertyMargin[] | undefined, ctx: HdBirthWindowPanelVmContext): string | undefined {
  if (lane.blocks.length > 1) return undefined;
  const margin = estimateMargins?.find(candidate => candidate.key === lane.key);
  if (!margin) return undefined;
  const candidates: number[] = [];
  if (margin.beforeMs !== undefined) candidates.push(-margin.beforeMs);
  if (margin.afterMs !== undefined) candidates.push(margin.afterMs);
  if (candidates.length === 0) return undefined;
  const nearest = candidates.sort((a, b) => Math.abs(a) - Math.abs(b))[0];
  return ctx.t('firstChange', { when: `≈ ${formatBirthWindowSignedDelta(nearest, ctx.lang)}` });
}

function propertyLanes(
  lanes: readonly HdBirthWindowLane[],
  tier: HdBirthWindowTier,
  estimateMargins: readonly HdBirthWindowPropertyMargin[] | undefined,
  ctx: HdBirthWindowPanelVmContext,
): HdPropertyLaneVm[] {
  return lanes
    .filter(lane => lane.tier === tier)
    .map<HdPropertyLaneVm>(lane => ({
      key: lane.key,
      label: ctx.labels.propertyLabel(lane.key, ctx.lang),
      blocks: lane.blocks.map(block => blockVm(lane, block, ctx)),
      hint: laneHint(lane, estimateMargins, ctx),
    }));
}

function planetName(sourceKey: string, ctx: HdBirthWindowPanelVmContext): string {
  return ctx.labels.planetLabel(sourceKey, ctx.lang);
}

function sideLabel(side: 'personality' | 'design', ctx: HdBirthWindowPanelVmContext): string {
  return ctx.t(`planets.${side}Track`);
}

/** Uitgeklapt: per planeet een lane met twee sporen (personality boven, design onder). */
function planetTrackLanes(planetLanes: readonly HdBirthWindowPlanetLane[], ctx: HdBirthWindowPanelVmContext): HdPropertyLaneVm[] {
  return planetLanes.map<HdPropertyLaneVm>(lane => {
    const name = planetName(lane.sourceKey, ctx);
    const tracks: HdPropertyLaneTrackVm[] = lane.tracks.map(track => ({
      key: `${lane.sourceKey}:${track.side}`,
      accent: track.side === 'design' ? HD_BIRTH_WINDOW_DESIGN_ACCENT : HD_BIRTH_WINDOW_PERSONALITY_ACCENT,
      blocks: track.blocks.map<HdPropertyLaneBlockVm>(block => ({
        fromMs: block.fromMs,
        toMs: block.toMs,
        label: `${block.gate}.${block.line}`,
        title: `${name} (${sideLabel(track.side, ctx)}): ${block.gate}.${block.line}\n${formatBlockRange(block, ctx)}`,
        state: block.state,
        data: {
          kind: 'planet', planetId: lane.planetId, sourceKey: lane.sourceKey, side: track.side,
          gate: block.gate, line: block.line, fromMs: block.fromMs, toMs: block.toMs, state: block.state,
        } satisfies HdBirthWindowPlanetBlockData,
      })),
    }));
    return { key: `planet:${lane.sourceKey}`, label: name, blocks: [], tracks };
  });
}

/** Ingeklapt: één lane met alle wissels als planeetglyph (zwart = personality, rood = design). */
function planetSummaryLane(changes: readonly HdBirthWindowPlanetChange[], ctx: HdBirthWindowPanelVmContext): HdPropertyLaneVm {
  const markers: HdPropertyLaneMarkerVm[] = changes.map(change => {
    const display = getHdPlanetDisplayConfigByPlanetId(change.planetId);
    const withDate = !sameBirthClockDay(change.ms, ctx.birthMs, ctx.timeZone);
    return {
      ms: change.ms,
      glyph: display?.astronomiconChar ?? '•',
      accent: change.side === 'design' ? HD_BIRTH_WINDOW_DESIGN_ACCENT : HD_BIRTH_WINDOW_PERSONALITY_ACCENT,
      // Korte vorm voor het wissellog van de verticale (phone-)weergave.
      shortLabel: `${change.side === 'design' ? 'D' : 'P'} ${change.from.gate}.${change.from.line} → ${change.to.gate}.${change.to.line}`,
      title: ctx.t('planets.changeTitle', {
        planet: planetName(change.sourceKey, ctx),
        side: sideLabel(change.side, ctx),
        from: `${change.from.gate}.${change.from.line}`,
        to: `${change.to.gate}.${change.to.line}`,
        time: formatBirthClock(change.ms, ctx.timeZone, ctx.lang, { seconds: true, date: withDate }),
      }),
    };
  });
  return { key: 'planets:summary', label: ctx.t('planets.summaryLane'), blocks: [], markers };
}

export interface HdBirthWindowGroupsInput {
  readonly lanes: readonly HdBirthWindowLane[];
  readonly estimateMargins: readonly HdBirthWindowPropertyMargin[] | undefined;
  readonly planetLanes: readonly HdBirthWindowPlanetLane[];
  readonly planetChanges: readonly HdBirthWindowPlanetChange[];
  readonly planetsExpanded: boolean;
  /** Getoonde lagen (het momentvenster heeft enkel Basics en Planeten); default alle. */
  readonly tiers?: readonly HdBirthWindowTier[];
}

/**
 * Lanes → groepen voor `hd-property-lanes`: Basics, Planeten (inklapbaar),
 * Variabelen en Base (gedempt). De schatting (ruimere horizon) levert de hint
 * "eerste wijziging ≈ +4 u 12 m" voor lanes zonder wissel binnen het venster.
 */
export function buildBirthWindowLaneGroups(input: HdBirthWindowGroupsInput, ctx: HdBirthWindowPanelVmContext): HdPropertyLaneGroupVm[] {
  return (input.tiers ?? HD_BIRTH_WINDOW_TIERS).map<HdPropertyLaneGroupVm>(tier => {
    const base = {
      key: tier,
      label: ctx.labels.tierLabel(tier, ctx.lang),
      hint: ctx.labels.tierHint(tier, ctx.lang, ctx.overlay),
      color: HD_BIRTH_WINDOW_TIER_COLORS[tier],
    };
    if (tier === 'planets') {
      return {
        ...base,
        lanes: planetTrackLanes(input.planetLanes, ctx),
        collapsible: true,
        collapsed: !input.planetsExpanded,
        collapsedLanes: [planetSummaryLane(input.planetChanges, ctx)],
        expandLabel: ctx.t('planets.expand'),
        collapseLabel: ctx.t('planets.collapse'),
      };
    }
    return {
      ...base,
      lanes: propertyLanes(input.lanes, tier, input.estimateMargins, ctx),
      muted: tier === 'base',
    };
  });
}
